/**
 * Bloqueos en la ficha (ADR-0018): «A bloqueada por B» = A espera a B.
 *
 * - «Bloqueada por»: las tareas que esta espera, con su estado, enlace y título cortado con «…». Las cerradas
 *   (hechas o descartadas) van tachadas porque ya no bloquean. Cualquiera con escritura quita (X) y agrega
 *   («Agregar bloqueo» abre `BuscadorTarea`): es colaboración, no se mira la autoría. LECTURA no ve botones.
 * - «Bloquea a»: las que esperan a esta. Solo lectura y solo si hay alguna.
 *
 * El buscador deshabilita lo que la API rechazaría («Ya la bloquea», «Es su madre o su hija», «Formaría un
 * ciclo», con el grafo de las listas en caché; ver `bloqueos.ts`). Lo que se escape lo dice la API.
 *
 * Foco: al cerrar el buscador (Esc, X o al elegir) vuelve a «Agregar bloqueo», y al quitar una fila va al
 * siguiente botón de quitar (o al título de la sección), igual que en «Subtareas».
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { formatearNumero, type ListaTareas, type TareaDetalle } from '@cdtec-backlog/contrato';
import { Icono } from '../../componentes/Icono';
import { grafoDeBloqueos, motivoNoBloqueante, type NodoBloqueos } from './reglasBloqueo';
import { BuscadorTarea } from './BuscadorTarea';
import { useAgregarBloqueo, useQuitarBloqueo } from './hooks';
import { ListaRelacionadas } from './ListaRelacionadas';
import './Bloqueos.css';

/** La tarea de la ficha: con lo que trae el detalle (`bloqueaA`). */
export type TareaConBloqueosDetalle = Pick<
  TareaDetalle,
  'id' | 'numero' | 'padre' | 'bloqueadaPor' | 'bloqueaA'
>;

interface Props {
  tarea: TareaConBloqueosDetalle;
  puedeEscribir: boolean;
  onAbrir?: (id: number) => void;
}

/** Nombre accesible del botón que quita un bloqueo. */
const etiquetaQuitar = (numero: number) => `Quitar el bloqueo de ${formatearNumero(numero)}`;

export function SeccionBloqueos({ tarea, puedeEscribir, onAbrir }: Props) {
  const cliente = useQueryClient();
  const [buscando, setBuscando] = useState(false);
  const agregar = useAgregarBloqueo();
  const quitar = useQuitarBloqueo();
  const ocupado = agregar.isPending || quitar.isPending;
  const seccion = useRef<HTMLElement>(null);
  const titulo = useRef<HTMLHeadingElement>(null);
  const botonAgregar = useRef<HTMLButtonElement>(null);
  // Adónde va el foco cuando termine lo que se está haciendo (ver el comentario del módulo).
  const foco = useRef<
    | { a: 'agregar' }
    | { a: 'quitar'; bloqueanteId: number; etiqueta: string; indice: number }
    | null
  >(null);
  useEffect(() => {
    const pedido = foco.current;
    if (!pedido || ocupado) return;
    if (pedido.a === 'agregar') {
      if (buscando || !botonAgregar.current) return;
      foco.current = null;
      botonAgregar.current.focus();
      return;
    }
    const botones = Array.from(
      seccion.current?.querySelectorAll<HTMLButtonElement>('.relacionadas__quitar') ?? [],
    );
    if (quitar.isError) {
      // Falló: la fila sigue; el foco vuelve a su botón.
      foco.current = null;
      botones.find((b) => b.getAttribute('aria-label') === pedido.etiqueta)?.focus();
      return;
    }
    // Hasta que el detalle se refresque, la fila quitada sigue en pantalla.
    if (tarea.bloqueadaPor.some((b) => b.id === pedido.bloqueanteId)) return;
    foco.current = null;
    (botones[Math.min(pedido.indice, botones.length - 1)] ?? titulo.current)?.focus();
  });

  const cerrarBuscador = () => {
    foco.current = { a: 'agregar' };
    setBuscando(false);
  };
  const bloqueada = { id: tarea.id, numero: tarea.numero };

  // El grafo «espera a» con lo que ya está en caché (listas del tablero, del buscador, subtareas). Solo hace
  // falta con el buscador abierto; se arma en cada dibujo para incluir los resultados recién llegados.
  const grafo = buscando
    ? grafoDeBloqueos(
        cliente
          .getQueriesData<ListaTareas>({ queryKey: ['tareas'] })
          .flatMap(([, lista]) => lista?.items ?? []),
      )
    : new Map<number, NodoBloqueos>();

  return (
    <>
      <section
        ref={seccion}
        className="bloqueos"
        aria-labelledby={`bloqueada-por-titulo-${tarea.id}`}
      >
        <h3
          ref={titulo}
          id={`bloqueada-por-titulo-${tarea.id}`}
          className="seccion-titulo"
          tabIndex={-1}
        >
          Bloqueada por
        </h3>
        {tarea.bloqueadaPor.length > 0 ? (
          <ListaRelacionadas
            tareas={tarea.bloqueadaPor}
            etiqueta={`Tareas que bloquean a ${formatearNumero(tarea.numero)}`}
            onAbrir={onAbrir}
            tacharCerradas
            quitar={(b) => (puedeEscribir ? etiquetaQuitar(b.numero) : null)}
            onQuitar={(b) => {
              const etiqueta = etiquetaQuitar(b.numero);
              const indice = Array.from(
                seccion.current?.querySelectorAll('.relacionadas__quitar') ?? [],
              ).findIndex((boton) => boton.getAttribute('aria-label') === etiqueta);
              foco.current = {
                a: 'quitar',
                bloqueanteId: b.id,
                etiqueta,
                indice: Math.max(indice, 0),
              };
              quitar.mutate({ bloqueada, bloqueante: { id: b.id, numero: b.numero } });
            }}
            ocupado={ocupado}
          />
        ) : (
          <p className="bloqueos__vacio">Nada la bloquea.</p>
        )}
        {puedeEscribir &&
          (buscando ? (
            <BuscadorTarea
              etiqueta="Buscar la tarea que la bloquea"
              placeholder="¿Qué tarea la bloquea? CDT-12 o título"
              tareaActualId={tarea.id}
              ocupado={agregar.isPending}
              motivoDeshabilitada={(candidata) => motivoNoBloqueante(tarea, candidata, grafo)}
              onElegir={(candidata) =>
                agregar.mutate(
                  { bloqueada, bloqueante: { id: candidata.id, numero: candidata.numero } },
                  { onSuccess: cerrarBuscador },
                )
              }
              onCerrar={cerrarBuscador}
            />
          ) : (
            <div className="bloqueos__botones">
              <button
                ref={botonAgregar}
                type="button"
                className="boton boton--con-icono"
                disabled={quitar.isPending}
                onClick={() => setBuscando(true)}
              >
                <Icono nombre="candado" />
                Agregar bloqueo
              </button>
            </div>
          ))}
      </section>
      {tarea.bloqueaA.length > 0 && (
        <section className="bloqueos" aria-labelledby={`bloquea-a-titulo-${tarea.id}`}>
          <h3 id={`bloquea-a-titulo-${tarea.id}`} className="seccion-titulo">
            Bloquea a
          </h3>
          <ListaRelacionadas
            tareas={tarea.bloqueaA}
            etiqueta={`Tareas que esperan a ${formatearNumero(tarea.numero)}`}
            onAbrir={onAbrir}
          />
        </section>
      )}
    </>
  );
}
